import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Film } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

/** Ridimensiona e comprime in WebP (max 960px, qualità 60%) — ~50% più leggero di JPEG. */
function compressImage(file, maxPx = 960, quality = 0.60) {
  const isPng = file.type === 'image/png';
  const supportsWebP = (() => {
    try { const c = document.createElement('canvas'); c.width = 1; c.height = 1; return c.toDataURL('image/webp').startsWith('data:image/webp'); }
    catch { return false; }
  })();
  const outputType = isPng ? 'image/png' : (supportsWebP ? 'image/webp' : 'image/jpeg');
  return new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) { height = Math.round((height * maxPx) / width); width = maxPx; }
        else { width = Math.round((width * maxPx) / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!isPng) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height); }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL(outputType, isPng ? undefined : quality));
    };
    img.onerror = reject;
    img.src = blobUrl;
  });
}

function isVideoUrl(url) {
  return url && (url.includes('/api/media/') || /\.(mp4|webm|mov|avi|ogg)(\?|$)/i.test(url));
}

/** Thumbnail video admin con anteprima 2s hover. */
function VideoThumb({ src }) {
  const vidRef = useRef(null);
  const timerRef = useRef(null);

  const startPreview = (e) => {
    e.stopPropagation();
    const v = vidRef.current;
    if (!v) return;
    clearTimeout(timerRef.current);
    v.currentTime = 0;
    v.play().catch(() => {});
    timerRef.current = setTimeout(() => {
      if (!vidRef.current) return;
      vidRef.current.pause();
      vidRef.current.currentTime = 0;
    }, 2000);
  };

  const stopPreview = () => {
    clearTimeout(timerRef.current);
    const v = vidRef.current;
    if (v) { v.pause(); v.currentTime = 0; }
  };

  return (
    <video
      ref={vidRef}
      src={src}
      className="w-full h-full object-cover"
      muted
      playsInline
      preload="metadata"
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
    />
  );
}

export default function AdminGalleria() {
  const PAGE_SIZE = 24;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [deleteId, setDeleteId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const fetchPage = useCallback(async (off = 0, replace = true) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/gallery`, { params: { limit: PAGE_SIZE, offset: off } });
      const data = res.data;
      setItems(prev => replace ? data : [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
      offsetRef.current = off + data.length;
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { fetchPage(0, true); }, [fetchPage]);

  const uploadOne = async (file) => {
    let mediaUrl = '';
    if (file.type.startsWith('image/')) {
      try { mediaUrl = await compressImage(file); }
      catch {
        mediaUrl = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = e => res(e.target.result);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
      }
    } else {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      mediaUrl = `${BACKEND_URL}${res.data.url}`;
    }
    await axios.post(`${API}/gallery`, { image_url: mediaUrl, caption: '', category: 'generale', order: 0 });
  };

  const processFiles = useCallback(async (files) => {
    const fileList = Array.from(files).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (fileList.length === 0) { toast.error('Seleziona immagini o video validi'); return; }
    setUploading(true);
    setProgress({ current: 0, total: fileList.length });
    let done = 0;
    const CONCURRENT = 3;

    for (let i = 0; i < fileList.length; i += CONCURRENT) {
      const batch = fileList.slice(i, i + CONCURRENT);
      const results = await Promise.allSettled(batch.map(f => uploadOne(f)));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') done++;
        else toast.error(`Errore con "${batch[idx].name}"`);
        setProgress(p => ({ ...p, current: p.current + 1 }));
      });
    }

    if (done > 0) toast.success(`${done} element${done !== 1 ? 'i' : 'o'} caricato${done !== 1 ? 'i' : ''}!`);
    setUploading(false);
    setProgress({ current: 0, total: 0 });
    fetchPage(0, true);
  }, [fetchPage]);

  const handleFileInput = (e) => {
    if (e.target.files?.length) processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };

  const handleDelete = async (id) => {
    const item = items.find(i => i.id === id);
    if (item && isVideoUrl(item.image_url) && item.image_url.includes('/api/media/')) {
      const mediaId = item.image_url.split('/api/media/')[1];
      try { await axios.delete(`${API}/media/${mediaId}`); } catch { /* ignora */ }
    }
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Elemento eliminato');
      setDeleteId(null);
      fetchPage(0, true);
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <div className="min-w-0 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900">Galleria</h2>
          <p className="font-body text-gray-500 text-xs mt-0.5">
            {items.length} element{items.length !== 1 ? 'i' : 'o'} nella galleria
          </p>
        </div>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-primary hover:bg-primary-dark text-white rounded-xl gap-2 flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{uploading ? `${progress.current}/${progress.total}…` : 'Aggiungi Media'}</span>
          <span className="sm:hidden">{uploading ? `${progress.current}/${progress.total}` : 'Aggiungi'}</span>
        </Button>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-5 sm:p-8 mb-4 text-center transition-colors select-none
          ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
          ${dragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'}`}
      >
        <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
        <p className="font-body text-gray-600 text-sm font-medium">
          {uploading ? `Caricamento ${progress.current} di ${progress.total}…` : 'Tocca per aggiungere foto o video'}
        </p>
        <p className="font-body text-gray-400 text-xs mt-0.5">JPG, PNG, WebP, MP4, WebM</p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handleFileInput}
      />

      {/* Progress Bar */}
      {uploading && progress.total > 0 && (
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
          <p className="font-body text-xs text-gray-500 mt-1 text-center">
            {progress.current} / {progress.total} file
          </p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-square bg-white rounded-xl animate-pulse border border-gray-100" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 font-body">Nessun elemento nella galleria</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => {
            const isVid = isVideoUrl(item.image_url);
            return (
              <div key={item.id} className="relative group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100">
                  {isVid ? (
                    <VideoThumb src={item.image_url} />
                  ) : (
                    <img
                      src={item.image_url}
                      alt="Galleria"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={e => { e.target.src = 'https://via.placeholder.com/200?text=No+img'; }}
                    />
                  )}
                </div>
                {isVid && (
                  <div className="absolute top-2 left-2 bg-black/60 rounded-lg p-1.5">
                    <Film className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setDeleteId(item.id)}
                    className="p-1.5 bg-white/90 hover:bg-red-50 rounded-lg text-red-500 shadow-sm transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
          );
          })}
        </div>
      )}

      {/* Load More */}
      {hasMore && !loading && (
        <div className="text-center mt-6">
          <Button
            onClick={() => fetchPage(offsetRef.current, false)}
            disabled={loadingMore}
            variant="outline"
            className="rounded-xl px-8 font-body"
          >
            {loadingMore ? 'Caricamento…' : 'Carica altri'}
          </Button>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold mb-2">Elimina Elemento</h3>
            <p className="font-body text-gray-500 mb-6">Sei sicuro? Questa azione è irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
